import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import { MenuItem, FormControl, InputLabel, Select, Typography, Box, Avatar, Chip, Divider, Paper } from "@material-ui/core";
import { Visibility, VisibilityOff, Settings, RecordVoiceOver, Close } from "@material-ui/icons";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PsychologyIcon from "@mui/icons-material/Psychology";
import { InputAdornment, IconButton } from "@material-ui/core";
import QueueSelectSingle from "../QueueSelectSingle";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: 20,
      overflow: "hidden",
      boxShadow: "0 32px 80px rgba(0,0,0,0.25)",
      maxHeight: "92vh",
    },
  },

  // ===== HEADER =====
  dialogHeader: {
    background: "linear-gradient(135deg, #0033CC 0%, #0066FF 50%, #00A3FF 100%)",
    padding: theme.spacing(3, 3, 2.5),
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: -40,
      right: -40,
      width: 160,
      height: 160,
      borderRadius: "50%",
      background: "rgba(255,255,255,0.06)",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: -20,
      right: 60,
      width: 80,
      height: 80,
      borderRadius: "50%",
      background: "rgba(255,255,255,0.04)",
    },
  },
  headerTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    position: "relative",
    zIndex: 1,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  headerAvatar: {
    width: 44,
    height: 44,
    background: "rgba(255,255,255,0.2)",
    border: "2px solid rgba(255,255,255,0.3)",
    fontSize: "1.3rem",
  },
  headerTitle: {
    color: "#ffffff",
    fontWeight: 800,
    fontSize: "1.15rem",
    letterSpacing: "-0.3px",
  },
  headerSubtitle: {
    color: "rgba(255,255,255,0.7)",
    fontSize: "0.75rem",
    marginTop: 2,
  },
  headerBadge: {
    background: "rgba(255,255,255,0.15)",
    color: "#ffffff",
    border: "1px solid rgba(255,255,255,0.25)",
    fontSize: "0.68rem",
    fontWeight: 600,
    height: 22,
  },
  closeButton: {
    color: "rgba(255,255,255,0.8)",
    padding: 6,
    "&:hover": {
      background: "rgba(255,255,255,0.15)",
      color: "#ffffff",
    },
  },

  // ===== CONTENT =====
  dialogContent: {
    padding: theme.spacing(3),
    background: theme.palette.background.default,
    overflowY: "auto",
    maxHeight: "65vh",
  },

  // ===== SECTIONS =====
  section: {
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2),
    background: theme.palette.background.paper,
    boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  sectionIcon: {
    width: 32,
    height: 32,
    background: "linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)",
    color: "#fff",
    fontSize: "0.9rem",
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: "0.85rem",
    color: theme.palette.text.primary,
  },
  sectionSubtitle: {
    fontSize: "0.73rem",
    color: theme.palette.text.secondary,
    marginTop: 1,
  },

  // ===== FIELDS =====
  fieldRow: {
    display: "flex",
    gap: theme.spacing(1.5),
    "& > *": { flex: 1 },
  },
  styledField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      fontSize: "0.85rem",
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#0066FF",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#0066FF",
        borderWidth: 2,
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#0066FF",
    },
  },
  promptField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      fontSize: "0.82rem",
      fontFamily: "'Fira Code', 'Courier New', monospace",
      lineHeight: 1.6,
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#0066FF",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#0066FF",
        borderWidth: 2,
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#0066FF",
    },
  },

  // ===== MODEL CHIPS =====
  modelGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  modelChip: {
    borderRadius: 8,
    fontWeight: 600,
    fontSize: "0.75rem",
    cursor: "pointer",
    border: `1px solid ${theme.palette.divider}`,
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: "#0066FF",
      background: "rgba(0,102,255,0.06)",
    },
  },
  modelChipSelected: {
    background: "linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)",
    color: "#ffffff !important",
    border: "none",
    boxShadow: "0 4px 12px rgba(0,102,255,0.35)",
  },
  modelChipGemini: {
    borderColor: "rgba(66,133,244,0.3)",
    "& .MuiChip-label": { color: "#4285F4" },
  },
  modelChipGeminiSelected: {
    background: "linear-gradient(135deg, #4285F4 0%, #34A853 100%)",
    color: "#ffffff !important",
    border: "none",
  },

  // ===== ACTIONS =====
  dialogActions: {
    padding: theme.spacing(2, 3),
    background: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    gap: theme.spacing(1),
  },
  cancelButton: {
    borderRadius: 10,
    fontWeight: 600,
    fontSize: "0.82rem",
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 2.5),
    "&:hover": {
      borderColor: "#FF4D4D",
      color: "#FF4D4D",
      background: "rgba(255,77,77,0.04)",
    },
  },
  saveButton: {
    borderRadius: 10,
    fontWeight: 700,
    fontSize: "0.82rem",
    background: "linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)",
    color: "#ffffff",
    padding: theme.spacing(1, 3),
    boxShadow: "0 6px 20px rgba(0,102,255,0.35)",
    "&:hover": {
      background: "linear-gradient(135deg, #0052CC 0%, #0088DD 100%)",
      boxShadow: "0 8px 24px rgba(0,102,255,0.45)",
    },
    "&:disabled": {
      background: theme.palette.action.disabledBackground,
      boxShadow: "none",
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: "#ffffff",
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const allowedModels = [
  "gpt-4.1-mini", "gpt-4.1", "gpt-4o", "gpt-4o-mini",
  "gemini-1.5-flash", "gemini-1.5-pro", "gemini-2.0-flash",
];

const modelLabels = {
  "gpt-4.1-mini": "Korax IA Lite",
  "gpt-4.1": "Korax IA Pro",
  "gpt-4o": "Korax IA Ultra",
  "gpt-4o-mini": "Korax IA Standard",
  "gemini-1.5-flash": "Korax IA Flash",
  "gemini-1.5-pro": "Korax IA Vision",
  "gemini-2.0-flash": "Korax IA Flash 2",
};

const PromptSchema = Yup.object().shape({
  name: Yup.string().min(5, "Muito curto!").max(100, "Muito longo!").required("Obrigatório"),
  prompt: Yup.string().min(50, "Muito curto!").required("Descreva o treinamento para IA"),
  model: Yup.string().oneOf(allowedModels, "Modelo inválido").required("Informe o modelo"),
  maxTokens: Yup.number().min(10).max(4096).required("Informe o máximo de tokens"),
  temperature: Yup.number().min(0).max(1).required("Informe a temperatura"),
  apiKey: Yup.string(),
  queueId: Yup.number().transform((v, o) => o === "" || o === null ? undefined : v).required("Informe a fila"),
  maxMessages: Yup.number().min(1).max(50).required("Informe o máximo de mensagens"),
  voice: Yup.string().when("model", {
    is: "gpt-4.1-mini",
    then: Yup.string().required("Informe o modo para Voz"),
    otherwise: Yup.string().notRequired(),
  }),
  voiceKey: Yup.string().notRequired(),
  voiceRegion: Yup.string().notRequired(),
});

const PromptModal = ({ open, onClose, promptId }) => {
  const classes = useStyles();
  const [showApiKey, setShowApiKey] = useState(false);

  const initialState = {
    name: "", prompt: "", model: "gpt-4.1-mini", voice: "texto",
    voiceKey: "", voiceRegion: "", maxTokens: 100, temperature: 1,
    apiKey: "", queueId: "", maxMessages: 10,
  };

  const [prompt, setPrompt] = useState(initialState);

  useEffect(() => {
    const fetchPrompt = async () => {
      if (!promptId) { setPrompt(initialState); return; }
      try {
        const { data } = await api.get(`/prompt/${promptId}`);
        setPrompt({ ...initialState, ...data, queueId: data?.queueId ?? "", model: allowedModels.includes(data.model) ? data.model : "gpt-4.1-mini" });
      } catch (err) { toastError(err); }
    };
    fetchPrompt();
  }, [promptId, open]);

  const handleClose = () => { setPrompt(initialState); onClose(); };

  const handleSavePrompt = async (values, { setSubmitting, setErrors }) => {
    try {
      const promptData = { ...values, temperature: Number(values.temperature), voice: values.model === "gpt-4.1-mini" ? values.voice : "texto" };
      if (promptId) { await api.put(`/prompt/${promptId}`, promptData); }
      else { await api.post("/prompt", promptData); }
      toast.success(i18n.t("promptModal.success"));
      handleClose();
    } catch (err) {
      toastError(err.response?.data?.message || "Erro ao salvar");
      setSubmitting(false);
    }
  };

  const isGemini = (model) => model && model.startsWith("gemini");

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" scroll="paper" fullWidth className={classes.dialog}>

      {/* ===== HEADER ===== */}
      <div className={classes.dialogHeader}>
        <div className={classes.headerTop}>
          <div className={classes.headerLeft}>
            <Avatar className={classes.headerAvatar}>🤖</Avatar>
            <div>
              <Typography className={classes.headerTitle}>
                {promptId ? "Editar Agente IA" : "Novo Agente IA"}
              </Typography>
              <Typography className={classes.headerSubtitle}>
                Configure o comportamento da Korax IA
              </Typography>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Chip label="Korax IA" size="small" className={classes.headerBadge} />
            <IconButton size="small" onClick={handleClose} className={classes.closeButton}>
              <Close fontSize="small" />
            </IconButton>
          </div>
        </div>
      </div>

      <Formik initialValues={prompt} enableReinitialize validationSchema={PromptSchema} onSubmit={handleSavePrompt}>
        {({ touched, errors, isSubmitting, values, setFieldValue }) => (
          <Form style={{ width: "100%" }}>
            <DialogContent className={classes.dialogContent}>

              {/* ===== SEÇÃO 1 — IDENTIDADE ===== */}
              <div className={classes.section}>
                <div className={classes.sectionHeader}>
                  <Avatar className={classes.sectionIcon}><PsychologyIcon style={{ fontSize: 16 }} /></Avatar>
                  <div>
                    <Typography className={classes.sectionTitle}>Identidade do Agente</Typography>
                    <Typography className={classes.sectionSubtitle}>Nome e chave de acesso à API</Typography>
                  </div>
                </div>
                <div className={classes.fieldRow}>
                  <Field as={TextField} label="Nome do Agente" name="name"
                    error={touched.name && Boolean(errors.name)} helperText={touched.name && errors.name}
                    variant="outlined" margin="dense" fullWidth required className={classes.styledField}
                  />

                </div>
              </div>

              {/* ===== SEÇÃO 2 — TREINAMENTO ===== */}
              <div className={classes.section}>
                <div className={classes.sectionHeader}>
                  <Avatar className={classes.sectionIcon}><SmartToyIcon style={{ fontSize: 16 }} /></Avatar>
                  <div>
                    <Typography className={classes.sectionTitle}>Treinamento da IA</Typography>
                    <Typography className={classes.sectionSubtitle}>Instruções de comportamento e personalidade</Typography>
                  </div>
                </div>
                <Field as={TextField} label="Prompt de Treinamento" name="prompt"
                  error={touched.prompt && Boolean(errors.prompt)} helperText={touched.prompt && errors.prompt}
                  variant="outlined" margin="dense" fullWidth required minRows={10} multiline
                  className={classes.promptField}
                  placeholder="Descreva como a IA deve se comportar, qual seu papel, tom de voz, fluxo de qualificação..."
                />
                <Box mt={1.5}>
                  <Field name="queueId" component={({ field, form }) => (
                    <QueueSelectSingle selectedQueueId={field.value} onChange={value => form.setFieldValue("queueId", value)} />
                  )} />
                </Box>
              </div>

              {/* ===== SEÇÃO 3 — MODELO ===== */}
              <div className={classes.section}>
                <div className={classes.sectionHeader}>
                  <Avatar className={classes.sectionIcon}><Settings style={{ fontSize: 16 }} /></Avatar>
                  <div>
                    <Typography className={classes.sectionTitle}>Modelo de IA</Typography>
                    <Typography className={classes.sectionSubtitle}>Selecione o motor de inteligência artificial</Typography>
                  </div>
                </div>
                <div className={classes.modelGrid}>
                  {allowedModels.map(model => (
                    <Chip
                      key={model}
                      label={modelLabels[model]}
                      onClick={() => { setFieldValue("model", model); if (model !== "gpt-4.1-mini") setFieldValue("voice", "texto"); }}
                      className={`${classes.modelChip} ${values.model === model ? (isGemini(model) ? classes.modelChipGeminiSelected : classes.modelChipSelected) : (isGemini(model) ? classes.modelChipGemini : "")}`}
                    />
                  ))}
                </div>
                {touched.model && errors.model && (
                  <Typography style={{ color: "#FF4D4D", fontSize: "0.72rem", marginTop: 4 }}>{errors.model}</Typography>
                )}

                <Box mt={2.5}>
                  <div className={classes.fieldRow}>
                    <Field as={TextField} label="Temperatura" name="temperature"
                      error={touched.temperature && Boolean(errors.temperature)} helperText={touched.temperature && errors.temperature}
                      variant="outlined" margin="dense" fullWidth type="number" inputProps={{ step: "0.1", min: "0", max: "1" }}
                      className={classes.styledField}
                    />
                    <Field as={TextField} label="Máx. Tokens" name="maxTokens"
                      error={touched.maxTokens && Boolean(errors.maxTokens)} helperText={touched.maxTokens && errors.maxTokens}
                      variant="outlined" margin="dense" fullWidth type="number" className={classes.styledField}
                    />
                    <Field as={TextField} label="Máx. Mensagens" name="maxMessages"
                      error={touched.maxMessages && Boolean(errors.maxMessages)} helperText={touched.maxMessages && errors.maxMessages}
                      variant="outlined" margin="dense" fullWidth type="number" className={classes.styledField}
                    />
                  </div>
                </Box>
              </div>

              {/* ===== SEÇÃO 4 — VOZ ===== */}
              {values.model === "gpt-4.1-mini" && (
                <div className={classes.section}>
                  <div className={classes.sectionHeader}>
                    <Avatar className={classes.sectionIcon}><RecordVoiceOver style={{ fontSize: 16 }} /></Avatar>
                    <div>
                      <Typography className={classes.sectionTitle}>Configuração de Voz</Typography>
                      <Typography className={classes.sectionSubtitle}>Habilite respostas em áudio via Azure Speech</Typography>
                    </div>
                  </div>
                  <div className={classes.fieldRow}>
                    <FormControl fullWidth margin="dense" variant="outlined" error={touched.voice && Boolean(errors.voice)} className={classes.styledField}>
                      <InputLabel>Modo de Voz</InputLabel>
                      <Field as={Select} label="Modo de Voz" name="voice">
                        <MenuItem value="texto">Apenas Texto</MenuItem>
                        <MenuItem value="pt-BR-FranciscaNeural">Francisca</MenuItem>
                        <MenuItem value="pt-BR-AntonioNeural">Antônio</MenuItem>
                        <MenuItem value="pt-BR-BrendaNeural">Brenda</MenuItem>
                        <MenuItem value="pt-BR-DonatoNeural">Donato</MenuItem>
                        <MenuItem value="pt-BR-ElzaNeural">Elza</MenuItem>
                        <MenuItem value="pt-BR-FabioNeural">Fábio</MenuItem>
                        <MenuItem value="pt-BR-GiovannaNeural">Giovanna</MenuItem>
                        <MenuItem value="pt-BR-HumbertoNeural">Humberto</MenuItem>
                        <MenuItem value="pt-BR-JulioNeural">Julio</MenuItem>
                        <MenuItem value="pt-BR-LeticiaNeural">Letícia</MenuItem>
                        <MenuItem value="pt-BR-YaraNeural">Yara</MenuItem>
                      </Field>
                    </FormControl>
                    <Field as={TextField} label="Chave Azure Speech" name="voiceKey"
                      variant="outlined" margin="dense" fullWidth className={classes.styledField}
                    />
                    <Field as={TextField} label="Região Azure" name="voiceRegion"
                      variant="outlined" margin="dense" fullWidth className={classes.styledField}
                    />
                  </div>
                </div>
              )}

            </DialogContent>

            {/* ===== ACTIONS ===== */}
            <DialogActions className={classes.dialogActions}>
              <Button onClick={handleClose} disabled={isSubmitting} className={classes.cancelButton} variant="outlined">
                Cancelar
              </Button>
              <div className={classes.btnWrapper}>
                <Button type="submit" disabled={isSubmitting} className={classes.saveButton} variant="contained">
                  {promptId ? "Salvar Alterações" : "Criar Agente IA"}
                  {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                </Button>
              </div>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default PromptModal;
